import { useState, useMemo, useCallback } from 'react';
import { useAdminProducts } from '@/hooks/useAdminProducts';
import { ProductDetail } from '@/mocks/productDetails';

type SortKey = 'stock' | 'name' | 'price' | 'category';
type SortDir = 'asc' | 'desc';
type StockFilter = 'all' | 'out' | 'low' | 'ok';

const CATEGORIES: Record<string, string> = {
  PS5: 'بلايستيشن 5',
  PS4: 'بلايستيشن 4',
  PC: 'كمبيوتر',
  Controllers: 'يد تحكم',
  Headsets: 'سماعات',
  Accessories: 'إكسسوارات',
};

function getStockStatus(stock: number): { label: string; color: string; bg: string; border: string; dot: string; level: number } {
  if (stock === 0) return { label: 'نفد', color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-500', level: 0 };
  if (stock <= 5) return { label: 'منخفض', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', dot: 'bg-amber-400', level: 1 };
  if (stock <= 20) return { label: 'متوسط', color: 'text-sky-700', bg: 'bg-sky-50', border: 'border-sky-200', dot: 'bg-sky-400', level: 2 };
  return { label: 'وفير', color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500', level: 3 };
}

interface Props {
  onToast: (msg: string, type?: 'success' | 'error') => void;
}

export default function StockReport({ onToast }: Props) {
  const { products, loading, refetch } = useAdminProducts();

  const [sortKey, setSortKey] = useState<SortKey>('stock');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [filter, setFilter] = useState<StockFilter>('all');
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);

  /* ── Stats ── */
  const stats = useMemo(() => {
    const total = products.length;
    const outOfStock = products.filter(p => (p.stock ?? 0) === 0).length;
    const lowStock = products.filter(p => { const s = p.stock ?? 0; return s > 0 && s <= 5; }).length;
    const totalUnits = products.reduce((sum, p) => sum + (p.stock ?? 0), 0);
    const totalValue = products.reduce((sum, p) => sum + (p.stock ?? 0) * p.price, 0);
    return { total, outOfStock, lowStock, totalUnits, totalValue };
  }, [products]);

  /* ── Filter + Sort ── */
  const filtered = useMemo(() => {
    let list = [...products];

    // text search
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(p =>
        p.nameAr.includes(search.trim()) ||
        p.category.toLowerCase().includes(q) ||
        (CATEGORIES[p.category] || '').includes(search.trim())
      );
    }

    // stock filter
    if (filter === 'out') list = list.filter(p => (p.stock ?? 0) === 0);
    else if (filter === 'low') list = list.filter(p => { const s = p.stock ?? 0; return s > 0 && s <= 5; });
    else if (filter === 'ok') list = list.filter(p => (p.stock ?? 0) > 5);

    // sort
    list.sort((a, b) => {
      let va: number | string = 0;
      let vb: number | string = 0;
      if (sortKey === 'stock') { va = a.stock ?? 0; vb = b.stock ?? 0; }
      else if (sortKey === 'name') { va = a.nameAr; vb = b.nameAr; }
      else if (sortKey === 'price') { va = a.price; vb = b.price; }
      else if (sortKey === 'category') { va = a.category; vb = b.category; }

      if (typeof va === 'string' && typeof vb === 'string') {
        return sortDir === 'asc' ? va.localeCompare(vb, 'ar') : vb.localeCompare(va, 'ar');
      }
      return sortDir === 'asc' ? (va as number) - (vb as number) : (vb as number) - (va as number);
    });

    return list;
  }, [products, search, filter, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortIcon = ({ k }: { k: SortKey }) => {
    if (sortKey !== k) return <i className="ri-arrow-up-down-line text-gray-300 text-xs"></i>;
    return <i className={`text-xs text-[#1a1a1a] ${sortDir === 'asc' ? 'ri-arrow-up-line' : 'ri-arrow-down-line'}`}></i>;
  };

  /* ── CSV Export ── */
  const exportCSV = useCallback(() => {
    setExporting(true);
    try {
      const headers = ['اسم المنتج', 'الفئة', 'الكمية المتاحة', 'الحالة', 'سعر البيع', 'قيمة المخزون', 'الشارة'];
      const rows = filtered.map(p => {
        const stock = p.stock ?? 0;
        const status = getStockStatus(stock);
        return [
          p.nameAr,
          CATEGORIES[p.category] || p.category,
          stock,
          status.label,
          `${p.price} ر.س`,
          `${(stock * p.price).toLocaleString('ar-SA')} ر.س`,
          p.badge || '—',
        ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',');
      });

      // Summary row
      rows.push('');
      rows.push([
        '"الإجمالي"', '""',
        `"${stats.totalUnits} وحدة"`,
        '""',
        '""',
        `"${stats.totalValue.toLocaleString('ar-SA')} ر.س"`,
        '""',
      ].join(','));

      const bom = '\uFEFF';
      const csv = bom + [headers.map(h => `"${h}"`).join(','), ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `تقرير_المخزون_${new Date().toLocaleDateString('ar-SA').replace(/\//g, '-')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      onToast('تم تصدير تقرير المخزون بنجاح ✓');
    } catch {
      onToast('حدث خطأ أثناء التصدير', 'error');
    }
    setExporting(false);
  }, [filtered, stats, onToast]);

  /* ── Bar chart: category breakdown ── */
  const categoryBreakdown = useMemo(() => {
    const map: Record<string, { total: number; out: number; low: number }> = {};
    products.forEach(p => {
      const cat = CATEGORIES[p.category] || p.category;
      if (!map[cat]) map[cat] = { total: 0, out: 0, low: 0 };
      const s = p.stock ?? 0;
      map[cat].total += s;
      if (s === 0) map[cat].out++;
      else if (s <= 5) map[cat].low++;
    });
    return Object.entries(map).sort((a, b) => b[1].total - a[1].total);
  }, [products]);

  const maxCatTotal = Math.max(...categoryBreakdown.map(c => c[1].total), 1);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-400">
        <i className="ri-loader-4-line text-3xl animate-spin"></i>
        <p className="text-sm">جاري تحميل تقرير المخزون...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-black text-[#1a1a1a] text-lg flex items-center gap-2">
            <i className="ri-archive-2-line text-emerald-600"></i>
            تقرير المخزون
          </h2>
          <p className="text-gray-400 text-xs mt-0.5">نظرة شاملة على مستويات المخزون لجميع المنتجات</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 font-bold text-sm px-3 py-2 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line text-base"></i>
            تحديث
          </button>
          <button
            onClick={exportCSV}
            disabled={exporting || products.length === 0}
            className="flex items-center gap-2 bg-[#1a1a1a] hover:bg-[#333] disabled:opacity-50 text-white font-bold text-sm px-3 py-2 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
          >
            {exporting
              ? <i className="ri-loader-4-line animate-spin text-base"></i>
              : <i className="ri-download-2-line text-base"></i>
            }
            تصدير CSV
          </button>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: 'إجمالي المنتجات', value: stats.total, icon: 'ri-shopping-bag-3-line', iconBg: 'bg-gray-50', iconColor: 'text-gray-600' },
          { label: 'نفد المخزون', value: stats.outOfStock, icon: 'ri-error-warning-line', iconBg: 'bg-red-50', iconColor: 'text-red-500', highlight: stats.outOfStock > 0 },
          { label: 'مخزون منخفض', value: stats.lowStock, icon: 'ri-alarm-warning-line', iconBg: 'bg-amber-50', iconColor: 'text-amber-500', highlight: stats.lowStock > 0 },
          { label: 'إجمالي الوحدات', value: stats.totalUnits.toLocaleString('ar-SA'), icon: 'ri-stack-line', iconBg: 'bg-emerald-50', iconColor: 'text-emerald-600' },
          { label: 'قيمة المخزون', value: `${stats.totalValue.toLocaleString('ar-SA')} ر.س`, icon: 'ri-money-dollar-circle-line', iconBg: 'bg-[#1a1a1a]', iconColor: 'text-white', dark: true },
        ].map(card => (
          <div
            key={card.label}
            className={`rounded-xl p-4 border ${card.dark ? 'bg-[#1a1a1a] border-transparent' : card.highlight ? 'bg-white border-red-100' : 'bg-white border-gray-100'}`}
          >
            <div className={`w-8 h-8 flex items-center justify-center rounded-lg mb-3 ${card.iconBg}`}>
              <i className={`${card.icon} text-sm ${card.iconColor}`}></i>
            </div>
            <p className={`text-lg font-black leading-tight ${card.dark ? 'text-white' : card.highlight ? 'text-red-600' : 'text-[#1a1a1a]'}`}>
              {card.value}
            </p>
            <p className={`text-xs mt-0.5 ${card.dark ? 'text-white/50' : 'text-gray-400'}`}>{card.label}</p>
          </div>
        ))}
      </div>

      {/* ── Category Breakdown Chart ── */}
      {categoryBreakdown.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <p className="text-sm font-bold text-[#1a1a1a] mb-4 flex items-center gap-2">
            <i className="ri-bar-chart-horizontal-line text-gray-400 text-sm"></i>
            توزيع المخزون حسب الفئة
          </p>
          <div className="flex flex-col gap-3">
            {categoryBreakdown.map(([cat, data]) => {
              const pct = Math.round((data.total / maxCatTotal) * 100);
              return (
                <div key={cat}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-[#1a1a1a]">{cat}</span>
                      {data.out > 0 && (
                        <span className="text-[10px] font-bold bg-red-50 text-red-600 border border-red-100 px-1.5 py-0.5 rounded-full">
                          {data.out} نفد
                        </span>
                      )}
                      {data.low > 0 && (
                        <span className="text-[10px] font-bold bg-amber-50 text-amber-600 border border-amber-100 px-1.5 py-0.5 rounded-full">
                          {data.low} منخفض
                        </span>
                      )}
                    </div>
                    <span className="text-xs font-black text-[#1a1a1a]">{data.total.toLocaleString('ar-SA')} وحدة</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${pct}%`,
                        background: data.out > 0
                          ? 'linear-gradient(90deg, #ef4444, #f87171)'
                          : data.low > 0
                          ? 'linear-gradient(90deg, #f59e0b, #fbbf24)'
                          : 'linear-gradient(90deg, #1a1a1a, #404040)',
                      }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Table ── */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        {/* Table header controls */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2 flex-wrap">
            {([
              { key: 'all', label: 'الكل', count: products.length },
              { key: 'out', label: 'نفد', count: stats.outOfStock },
              { key: 'low', label: 'منخفض', count: stats.lowStock },
              { key: 'ok', label: 'متوفر', count: products.length - stats.outOfStock - stats.lowStock },
            ] as { key: StockFilter; label: string; count: number }[]).map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                  filter === f.key
                    ? f.key === 'out' ? 'bg-red-500 text-white'
                    : f.key === 'low' ? 'bg-amber-400 text-black'
                    : f.key === 'ok' ? 'bg-emerald-500 text-white'
                    : 'bg-[#1a1a1a] text-white'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                {f.label}
                <span className={`text-[10px] font-black px-1 py-0.5 rounded-full min-w-[18px] text-center ${
                  filter === f.key ? 'bg-white/20' : 'bg-white text-gray-600'
                }`}>
                  {f.count}
                </span>
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-48">
            <i className="ri-search-line absolute top-1/2 -translate-y-1/2 right-3 text-gray-300 text-sm"></i>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="بحث..."
              className="w-full border border-gray-200 rounded-lg pr-8 pl-3 py-2 text-sm outline-none focus:border-[#1a1a1a] transition-colors"
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-300">
            <i className="ri-inbox-line text-4xl mb-3"></i>
            <p className="text-sm">لا توجد منتجات مطابقة</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" dir="rtl">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-right text-[11px] font-bold text-gray-400 px-5 py-3 whitespace-nowrap">
                    <button onClick={() => toggleSort('name')} className="flex items-center gap-1.5 cursor-pointer hover:text-gray-700 transition-colors">
                      المنتج <SortIcon k="name" />
                    </button>
                  </th>
                  <th className="text-right text-[11px] font-bold text-gray-400 px-4 py-3 whitespace-nowrap">
                    <button onClick={() => toggleSort('category')} className="flex items-center gap-1.5 cursor-pointer hover:text-gray-700 transition-colors">
                      الفئة <SortIcon k="category" />
                    </button>
                  </th>
                  <th className="text-right text-[11px] font-bold text-gray-400 px-4 py-3 whitespace-nowrap">
                    <button onClick={() => toggleSort('stock')} className="flex items-center gap-1.5 cursor-pointer hover:text-gray-700 transition-colors">
                      الكمية <SortIcon k="stock" />
                    </button>
                  </th>
                  <th className="text-right text-[11px] font-bold text-gray-400 px-4 py-3 whitespace-nowrap">الحالة</th>
                  <th className="text-right text-[11px] font-bold text-gray-400 px-4 py-3 whitespace-nowrap">
                    <button onClick={() => toggleSort('price')} className="flex items-center gap-1.5 cursor-pointer hover:text-gray-700 transition-colors">
                      سعر البيع <SortIcon k="price" />
                    </button>
                  </th>
                  <th className="text-right text-[11px] font-bold text-gray-400 px-4 py-3 whitespace-nowrap">قيمة المخزون</th>
                  <th className="text-right text-[11px] font-bold text-gray-400 px-4 py-3 whitespace-nowrap">مستوى المخزون</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((product: ProductDetail, idx: number) => {
                  const stock = product.stock ?? 0;
                  const status = getStockStatus(stock);
                  const stockValue = stock * product.price;
                  const maxStock = Math.max(...products.map(p => p.stock ?? 0), 1);
                  const barPct = Math.round((stock / maxStock) * 100);

                  return (
                    <tr
                      key={product.id}
                      className={`border-b border-gray-50 hover:bg-gray-50/60 transition-colors ${idx % 2 === 0 ? '' : 'bg-gray-50/20'}`}
                    >
                      {/* Product */}
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                            {product.images?.[0] ? (
                              <img src={product.images[0]} alt={product.nameAr} className="w-full h-full object-cover object-top" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <i className="ri-image-line text-gray-300"></i>
                              </div>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-bold text-[#1a1a1a] truncate max-w-[160px]">{product.nameAr}</p>
                            {product.badge && (
                              <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-100 px-1.5 py-0.5 rounded-full">
                                {product.badge}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Category */}
                      <td className="px-4 py-3">
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-md whitespace-nowrap">
                          {CATEGORIES[product.category] || product.category}
                        </span>
                      </td>

                      {/* Quantity */}
                      <td className="px-4 py-3">
                        <span className={`text-sm font-black ${stock === 0 ? 'text-red-600' : stock <= 5 ? 'text-amber-600' : 'text-[#1a1a1a]'}`}>
                          {stock.toLocaleString('ar-SA')}
                        </span>
                        <span className="text-xs text-gray-400 mr-1">وحدة</span>
                      </td>

                      {/* Status badge */}
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full border ${status.bg} ${status.color} ${status.border} whitespace-nowrap`}>
                          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${status.dot} ${stock > 0 && stock <= 5 ? 'animate-pulse' : ''}`}></span>
                          {status.label}
                        </span>
                      </td>

                      {/* Price */}
                      <td className="px-4 py-3">
                        <span className="text-xs font-bold text-[#1a1a1a] whitespace-nowrap">
                          {product.price.toLocaleString('ar-SA')} ر.س
                        </span>
                      </td>

                      {/* Stock value */}
                      <td className="px-4 py-3">
                        <span className={`text-xs font-black whitespace-nowrap ${stock === 0 ? 'text-gray-300' : 'text-[#1a1a1a]'}`}>
                          {stock === 0 ? '—' : `${stockValue.toLocaleString('ar-SA')} ر.س`}
                        </span>
                      </td>

                      {/* Stock level bar */}
                      <td className="px-4 py-3 min-w-[120px]">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${
                                stock === 0 ? 'bg-red-400' : stock <= 5 ? 'bg-amber-400' : stock <= 20 ? 'bg-sky-400' : 'bg-emerald-500'
                              }`}
                              style={{ width: `${Math.max(barPct, stock > 0 ? 3 : 0)}%` }}
                            ></div>
                          </div>
                          <span className="text-[10px] text-gray-400 w-8 text-left flex-shrink-0">{barPct}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>

              {/* Footer totals */}
              <tfoot>
                <tr className="bg-[#1a1a1a]">
                  <td colSpan={2} className="px-5 py-3 text-white text-xs font-bold">
                    الإجمالي — {filtered.length} منتج
                  </td>
                  <td className="px-4 py-3 text-amber-400 font-black text-sm whitespace-nowrap">
                    {filtered.reduce((s, p) => s + (p.stock ?? 0), 0).toLocaleString('ar-SA')} وحدة
                  </td>
                  <td className="px-4 py-3"></td>
                  <td className="px-4 py-3"></td>
                  <td className="px-4 py-3 text-amber-400 font-black text-sm whitespace-nowrap">
                    {filtered.reduce((s, p) => s + (p.stock ?? 0) * p.price, 0).toLocaleString('ar-SA')} ر.س
                  </td>
                  <td className="px-4 py-3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
